import React from 'react';
import { Collapse, DatePicker, Form, Input, Select, Space, Switch, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { SupplierOptionDto } from '../../../dto/reference';
import { formatDate } from '../../../utils/dateUtils';
import { OrderDto } from '../dto';
import { customerOrderNumberRule } from '../../../dto/order';
import { getOrderForecastMonth, OrderDetailEditableField } from '../orderDetailEditUtils';
import {
  ORDER_DELIVERY_MODES,
  orderDeliveryModeLabelKey,
} from '../../transports/transportDirection';
import {
  DETAIL_ROW_GRID_STYLE,
  ORDER_INFO_ACCORDION_STYLE,
  ORDER_INFO_SUMMARY_GRID_STYLE,
  renderDetailRow,
  renderSectionTitle,
  renderSummaryItem,
} from '../orderDetailLayout';

interface AccountManagerOption {
  id: string;
  name: string;
  email?: string;
}

interface OrderInfoAccordionProps {
  order: OrderDto;
  customerName: string;
  createdByName: string;
  accountManagers: AccountManagerOption[];
  suppliers: SupplierOptionDto[];
  customerPaymentStatus: 'PENDING' | 'PAID';
  canViewCustomerPayments: boolean;
  isCustomerPaymentOverdue: boolean;
  useManualNumberingValue: boolean;
  editingField: OrderDetailEditableField | null;
  editForm: ReturnType<typeof Form.useForm>[0];
  renderEditableField: (
    field: OrderDetailEditableField,
    value: React.ReactNode,
    renderComponent: () => React.ReactNode,
    canEditField?: boolean
  ) => React.ReactNode;
  /** Commercial header: account manager, dates, number, supplier, producer, locations. */
  canEditCommercialFields: boolean;
  /** Account manager (opiekun) field specifically — false for Handlowiec (ORDER_STAFF). */
  canEditAccountManager: boolean;
  canEditCustomerOrderDocuments: boolean;
  /** WP13: forecast month — ADMIN and OPERATIONS change it; others read it. */
  canEditForecastMonth: boolean;
  /** WP14: "Dostawa" — ADMIN, OPERATIONS and Handlowiec (own orders). */
  canEditDeliveryMode?: boolean;
}

const ORDER_INFO_DEFAULT_OPEN_SECTIONS: string[] = [];

export const OrderInfoAccordion: React.FC<OrderInfoAccordionProps> = ({
  order,
  customerName,
  createdByName,
  accountManagers,
  suppliers,
  customerPaymentStatus,
  canViewCustomerPayments,
  isCustomerPaymentOverdue,
  useManualNumberingValue,
  editingField,
  editForm,
  renderEditableField,
  canEditCommercialFields,
  canEditAccountManager,
  canEditCustomerOrderDocuments,
  canEditForecastMonth,
  canEditDeliveryMode = false,
}) => {
  const { t } = useTranslation(['orders', 'common']);
  const forecastMonth = getOrderForecastMonth(order);
  const forecastMonthLabel = (
    <Space wrap size={6}>
      <span>{forecastMonth ? forecastMonth.format('MM.YYYY') : '-'}</span>
      {!order.forecastMonth && (
        <Tag>{t('orders:orderDetail.forecastMonthDefault')}</Tag>
      )}
    </Space>
  );

  return (
    <>
      <div style={ORDER_INFO_SUMMARY_GRID_STYLE}>
        {renderSummaryItem(t('orders:orderDetail.customer'), customerName)}
        {renderSummaryItem(t('orders:orderDetail.currency'), order.currency || 'PLN')}
        {renderSummaryItem(t('orders:orderDetail.orderDate'), formatDate(order.orderDate))}
        {/* WP14: always visible — self-pickup changes what the order needs next. */}
        {renderSummaryItem(
          t('orders:deliveryMode.label'),
          renderEditableField(
            'deliveryMode',
            <Tag color={order.deliveryMode === 'CUSTOMER_PICKUP' ? 'gold' : undefined} data-testid="order-delivery-mode">
              {t(orderDeliveryModeLabelKey(order.deliveryMode || 'OUR_DELIVERY'))}
            </Tag>,
            () => (
              <Form form={editForm} style={{ display: 'inline-block' }}>
                <Form.Item name="deliveryMode" style={{ margin: 0 }}>
                  <Select
                    style={{ width: 220 }}
                    className="order-delivery-mode-select"
                    aria-label={t('orders:deliveryMode.label')}
                    options={ORDER_DELIVERY_MODES.map((mode) => ({
                      value: mode,
                      label: t(orderDeliveryModeLabelKey(mode)),
                    }))}
                  />
                </Form.Item>
              </Form>
            ),
            canEditDeliveryMode
          )
        )}
        {canViewCustomerPayments && renderSummaryItem(
          t('orders:orderDetail.customerPaymentStatus'),
          <Tag color={customerPaymentStatus === 'PAID' ? 'green' : 'orange'}>
            {t(`orders:customerPaymentStatus.${customerPaymentStatus}`)}
          </Tag>
        )}
      </div>

      <Collapse
        defaultActiveKey={ORDER_INFO_DEFAULT_OPEN_SECTIONS}
        style={{ ...ORDER_INFO_ACCORDION_STYLE, marginBottom: 16 }}
        items={[
          {
            key: 'basic',
            label: renderSectionTitle(t('orders:orderDetail.basicInfo')),
            children: (
              <div style={DETAIL_ROW_GRID_STYLE}>
                {renderDetailRow(t('orders:orderDetail.customer'), customerName)}
                {renderDetailRow(t('orders:orderDetail.currency'), order.currency || 'PLN')}
                {renderDetailRow(t('orders:orderDetail.createdBy'), createdByName)}
                {renderDetailRow(
                  t('orders:orderDetail.accountManager'),
                  renderEditableField(
                    'accountManagerId',
                    order.accountManagerName || '-',
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item name="accountManagerId" style={{ margin: 0 }}>
                          <Select
                            style={{ width: 260 }}
                            placeholder={t('orders:modal.selectAccountManager')}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                          >
                            {accountManagers.map((manager) => (
                              <Select.Option key={manager.id} value={manager.id}>
                                {manager.name}
                                {manager.email ? ` (${manager.email})` : ''}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Form>
                    ),
                    canEditAccountManager
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.description'),
                  renderEditableField(
                    'description',
                    order.description,
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block', width: '100%' }}>
                        <Form.Item
                          name="description"
                          style={{ margin: 0 }}
                          rules={[{ required: true, message: t('orders:orderDetail.messages.descriptionRequired') }]}
                        >
                          <Input.TextArea rows={2} style={{ width: '100%', maxWidth: 560 }} />
                        </Form.Item>
                      </Form>
                    )
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.notes'),
                  renderEditableField(
                    'notes',
                    order.notes || '-',
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block', width: '100%' }}>
                        <Form.Item name="notes" style={{ margin: 0 }}>
                          <Input.TextArea rows={2} style={{ width: '100%', maxWidth: 560 }} />
                        </Form.Item>
                      </Form>
                    )
                  )
                )}
              </div>
            ),
          },
          {
            key: 'dates',
            label: renderSectionTitle(t('orders:orderDetail.datesInfo')),
            children: (
              <div style={DETAIL_ROW_GRID_STYLE}>
                {renderDetailRow(
                  t('orders:orderDetail.orderDate'),
                  renderEditableField(
                    'orderDate',
                    formatDate(order.orderDate),
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item
                          name="orderDate"
                          style={{ margin: 0 }}
                          rules={[{ required: true, message: t('orders:orderDetail.messages.orderDateRequired') }]}
                        >
                          <DatePicker />
                        </Form.Item>
                      </Form>
                    ),
                    canEditCommercialFields
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.forecastMonth'),
                  renderEditableField(
                    'forecastMonth',
                    forecastMonthLabel,
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item
                          name="forecastMonth"
                          style={{ margin: 0 }}
                          extra={t('orders:orderDetail.forecastMonthHint')}
                        >
                          <DatePicker
                            picker="month"
                            format="MM.YYYY"
                            allowClear
                            aria-label={t('orders:orderDetail.forecastMonth')}
                          />
                        </Form.Item>
                      </Form>
                    ),
                    canEditForecastMonth
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.expectedDelivery'),
                  renderEditableField(
                    'expectedDelivery',
                    formatDate(order.expectedDelivery),
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item name="expectedDelivery" style={{ margin: 0 }}>
                          <DatePicker />
                        </Form.Item>
                      </Form>
                    )
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.actualDelivery'),
                  renderEditableField(
                    'actualDelivery',
                    formatDate(order.actualDelivery),
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item name="actualDelivery" style={{ margin: 0 }}>
                          <DatePicker />
                        </Form.Item>
                      </Form>
                    )
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.estimatedCompletionDate'),
                  renderEditableField(
                    'estimatedCompletionDate',
                    formatDate(order.estimatedCompletionDate),
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item name="estimatedCompletionDate" style={{ margin: 0 }}>
                          <DatePicker />
                        </Form.Item>
                      </Form>
                    )
                  )
                )}
              </div>
            ),
          },
          ...(canViewCustomerPayments ? [{
            key: 'payments',
            label: renderSectionTitle(t('orders:orderDetail.paymentInfo')),
            children: (
              <div style={DETAIL_ROW_GRID_STYLE}>
                {renderDetailRow(
                  t('orders:orderDetail.customerPaymentDueDate'),
                  renderEditableField(
                    'customerPaymentDueDate',
                    <Space wrap>
                      <span>{formatDate(order.customerPaymentDueDate)}</span>
                      {isCustomerPaymentOverdue && <Tag color="red">{t('orders:orderDetail.customerPaymentOverdue')}</Tag>}
                    </Space>,
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item name="customerPaymentDueDate" style={{ margin: 0 }}>
                          <DatePicker />
                        </Form.Item>
                      </Form>
                    )
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.customerPaymentStatus'),
                  renderEditableField(
                    'customerPaymentStatus',
                    <Tag color={customerPaymentStatus === 'PAID' ? 'green' : 'orange'}>
                      {t(`orders:customerPaymentStatus.${customerPaymentStatus}`)}
                    </Tag>,
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item name="customerPaymentStatus" style={{ margin: 0 }}>
                          <Select style={{ width: 180 }}>
                            <Select.Option value="PENDING">{t('orders:customerPaymentStatus.PENDING')}</Select.Option>
                            <Select.Option value="PAID">{t('orders:customerPaymentStatus.PAID')}</Select.Option>
                          </Select>
                        </Form.Item>
                      </Form>
                    )
                  )
                )}
                {renderDetailRow(t('orders:orderDetail.customerPaymentPaidAt'), formatDate(order.customerPaymentPaidAt))}
                {renderDetailRow(
                  t('orders:orderDetail.customerPaymentNotes'),
                  renderEditableField(
                    'customerPaymentNotes',
                    order.customerPaymentNotes || '-',
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block', width: '100%' }}>
                        <Form.Item name="customerPaymentNotes" style={{ margin: 0 }}>
                          <Input.TextArea rows={2} style={{ width: '100%', maxWidth: 560 }} />
                        </Form.Item>
                      </Form>
                    )
                  )
                )}
              </div>
            ),
          }] : []),
          {
            key: 'production',
            label: renderSectionTitle(t('orders:orderDetail.productionLogistics')),
            children: (
              <div style={DETAIL_ROW_GRID_STYLE}>
                {renderDetailRow(
                  t('orders:orderDetail.useManualNumbering'),
                  renderEditableField(
                    'manualNumbering',
                    order.useManualNumbering ? t('common:yes') : t('common:no'),
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Space wrap>
                          <Form.Item name="useManualNumbering" valuePropName="checked" style={{ margin: 0 }}>
                            <Switch />
                          </Form.Item>
                          {useManualNumberingValue && (
                            <Form.Item
                              name="manualOrderNumber"
                              style={{ margin: 0 }}
                              rules={[{
                                required: true,
                                whitespace: true,
                                message: t('orders:modal.validation.manualNumberRequired')
                              }, customerOrderNumberRule(t)]}
                            >
                              <Input maxLength={64} placeholder={t('orders:orderDetail.manualOrderNumberPlaceholder')} style={{ width: 220 }} />
                            </Form.Item>
                          )}
                        </Space>
                      </Form>
                    ),
                    canEditCommercialFields
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.clientOrderNumber'),
                  renderEditableField(
                    'clientOrderNumber',
                    order.clientOrderNumber || '-',
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item name="clientOrderNumber" style={{ margin: 0 }}>
                          <Input style={{ width: 220 }} />
                        </Form.Item>
                      </Form>
                    )
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.supplier'),
                  renderEditableField(
                    'supplierId',
                    suppliers.find(s => s.id === order.supplierId)?.name || order.producer || '-',
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item name="supplierId" style={{ margin: 0 }}>
                          <Select
                            style={{ width: 260 }}
                            placeholder={t('orders:orderDetail.selectSupplier')}
                            allowClear
                            showSearch
                            optionFilterProp="searchText"
                          >
                            {suppliers.map(supplier => (
                              <Select.Option key={supplier.id} value={supplier.id} searchText={supplier.searchText}>
                                {supplier.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Form>
                    ),
                    canEditCommercialFields
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.producer'),
                  renderEditableField(
                    'producer',
                    order.producer || '-',
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item name="producer" style={{ margin: 0 }}>
                          <Input style={{ width: 260 }} />
                        </Form.Item>
                      </Form>
                    ),
                    canEditCommercialFields
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.factoryLocation'),
                  renderEditableField(
                    'factoryLocation',
                    order.factoryLocation || '-',
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block', width: '100%' }}>
                        <Form.Item name="factoryLocation" style={{ margin: 0 }}>
                          <Input.TextArea rows={2} style={{ width: '100%', maxWidth: 560 }} />
                        </Form.Item>
                      </Form>
                    ),
                    canEditCommercialFields
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.deliveryLocation'),
                  renderEditableField(
                    'deliveryLocation',
                    order.deliveryLocation || '-',
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block', width: '100%' }}>
                        <Form.Item name="deliveryLocation" style={{ margin: 0 }}>
                          <Input.TextArea rows={2} style={{ width: '100%', maxWidth: 560 }} />
                        </Form.Item>
                      </Form>
                    ),
                    canEditCommercialFields
                  )
                )}
              </div>
            ),
          },
          {
            key: 'documents',
            label: renderSectionTitle(t('orders:orderDetail.documentsInfo')),
            children: (
              <div style={DETAIL_ROW_GRID_STYLE}>
                {renderDetailRow(
                  t('orders:orderDetail.proformaNumber'),
                  renderEditableField(
                    'proforma',
                    order.proformaNumber || '-',
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item name="proformaNumber" style={{ margin: 0 }}>
                          <Input placeholder={t('orders:orderDetail.number')} />
                        </Form.Item>
                      </Form>
                    ),
                    canEditCustomerOrderDocuments
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.proformaDate'),
                  editingField === 'proforma' ? (
                    <Form form={editForm} style={{ display: 'inline-block' }}>
                      <Form.Item name="proformaDate" style={{ margin: 0 }}>
                        <DatePicker />
                      </Form.Item>
                    </Form>
                  ) : (
                    formatDate(order.proformaDate)
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.invoiceNumber'),
                  renderEditableField(
                    'invoice',
                    order.invoiceNumber || '-',
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item name="invoiceNumber" style={{ margin: 0 }}>
                          <Input placeholder={t('orders:orderDetail.number')} />
                        </Form.Item>
                      </Form>
                    ),
                    canEditCustomerOrderDocuments
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.invoiceDate'),
                  editingField === 'invoice' ? (
                    <Form form={editForm} style={{ display: 'inline-block' }}>
                      <Form.Item name="invoiceDate" style={{ margin: 0 }}>
                        <DatePicker />
                      </Form.Item>
                    </Form>
                  ) : (
                    formatDate(order.invoiceDate)
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.waybillNumber'),
                  renderEditableField(
                    'waybill',
                    order.waybillNumber || '-',
                    () => (
                      <Form form={editForm} style={{ display: 'inline-block' }}>
                        <Form.Item name="waybillNumber" style={{ margin: 0 }}>
                          <Input placeholder={t('orders:orderDetail.number')} />
                        </Form.Item>
                      </Form>
                    ),
                    canEditCustomerOrderDocuments
                  )
                )}
                {renderDetailRow(
                  t('orders:orderDetail.waybillDate'),
                  editingField === 'waybill' ? (
                    <Form form={editForm} style={{ display: 'inline-block' }}>
                      <Form.Item name="waybillDate" style={{ margin: 0 }}>
                        <DatePicker />
                      </Form.Item>
                    </Form>
                  ) : (
                    formatDate(order.waybillDate)
                  )
                )}
              </div>
            ),
          },
        ]}
      />
    </>
  );
};
